import { useEffect, useId, useRef, useState, type Ref, type SyntheticEvent } from "react";

import { composeRippleProps, useTouchRipple } from "@/components/touch-ripple";
import { CircularProgress } from "@/components/ui/circular-progress";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { IconButton } from "@/components/ui/icon-button";
import { MuiButton } from "@/components/ui/mui-button";
import { MuiCheckBoxIcon, MuiCheckBoxOutlineBlankIcon } from "@/components/ui/mui-checkbox-icons";
import { MuiAddIcon } from "@/components/ui/mui-community-icons";
import { MuiCheckIcon } from "@/components/ui/mui-field-icons";
import { MUI_MENU_ITEM } from "@/components/ui/mui-field-shape";
import { MuiMultiSelect } from "@/components/ui/mui-multi-select";
import {
  MuiRadioCheckedMediumIcon,
  MuiRadioUncheckedMediumIcon,
} from "@/components/ui/mui-radio-icons";
import { MuiSelectCaret } from "@/components/ui/mui-select-caret";
import { MuiDeleteOutlineOutlinedIcon, MuiEditIcon } from "@/components/ui/mui-spec-icons";
import { OutlinedField } from "@/components/ui/outlined-field";
import { TextField } from "@/components/ui/text-field";
import {
  useCreateSpecLocation,
  useDeleteSpecLocation,
  useRenameSpecLocation,
  useUpdateSpecLineLocations,
  useUpdateSpecLineLocationType,
} from "@/hooks/spec-editor-locations";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import type { SpecLinesLine, SpecLinesLocation } from "@/hooks/specifications";
import { ERRORS } from "@/lib/spec-editor-errors";
import {
  checkedPlacements,
  locationsSelectText,
  type LineLocationType,
} from "@/lib/spec-lines-display";
import { cn } from "@/lib/utils";

// The Editor's locations select (#808) — the donor's `LineLocations`
// (`SpecEditIsland/components/MainContent/Lines/LineLocations/
// LineLocations.tsx`), in a line row's name cell (#810 mounts it), wired to
// #802's location verbs. The parity enumeration on #808 (E1–E24) names the
// source line of every fact below.
//
// **The field**: an outlined small `Select multiple displayEmpty` with no
// label, its menu opening in place (`disablePortal`) at MUI's default scroll
// lock. Closed it reads the line's type, or for a `specific` line its one
// checked room's name, else `${n} Locations` — `0 Locations` included; a
// placement counts only while its room is in the version's list. It is
// disabled only on a finalized line — a per-plan line keeps it, and it never
// carries a tooltip — and red while the type or the checklist error shows.
// Every click in it stops there, so the row's Active-selection click never
// fires.
//
// **The menu**:
//
//   - **three radios** — `Applicable Locations`, `Per Plan`, `Specific
//     Locations` — checked by the line's type, the third ruled off in
//     `#DEDEDE` while the line is specific. A pick sends the type at once
//     (optimistic, #802), even the current one (carried, Assumption 11), and
//     closes the menu unless it is `Specific Locations`;
//   - **the checklist**, while specific: every room of the version in the
//     cached list's order, checked when the line has it. A toggle sends the
//     ticked ids and shows the server's answer, never a guess;
//   - **a room's row**: on hover, the delete glyph and the pencil. The pencil
//     turns the row into a name field (see `RoomRow`); the delete asks first,
//     in the shared confirm, with the ruled copy, and while the delete runs
//     the row reads `Deleting...` over a spinner;
//   - **`+ Add Location`**, at the foot, a tab stop off the arrows' walk: a
//     name field whose submit closes at once, the button reading `Adding...`
//     until the room arrives ticked.
//
// **Every value on screen is the cache's** (ADR-0014 decision 1): the closed
// label, the checklist and each row's name follow a rename's guess, a
// result, or a rollback, with no local copy — the ruled stale-screen repair.

/** `LocationTypeMapping` in the radios' order (`LineLocations.tsx:157-192`). */
const TYPE_RADIOS: readonly { value: LineLocationType; label: string }[] = [
  { value: "applicable", label: "Applicable Locations" },
  { value: "per_plan", label: "Per Plan" },
  { value: "specific", label: "Specific Locations" },
];

/** The confirm's clause after the question — the selections rulings' copy. */
const LOCATION_DELETE_DESCRIPTION =
  "It will be removed from every Line in this version and its notes will be lost.";

export interface SpecLineLocationsProps {
  /** The line as the cached lines page holds it. */
  line: SpecLinesLine;
  /** The version's rooms in the session's order (`useSpecLinesLoader`'s
   *  `rooms`; #965 N12). */
  rooms: readonly SpecLinesLocation[];
  target: SpecEditorTarget;
}

export function SpecLineLocations({ line, rooms, target }: SpecLineLocationsProps) {
  const displayId = useId();
  const updateType = useUpdateSpecLineLocationType();
  const updateRooms = useUpdateSpecLineLocations();
  const rename = useRenameSpecLocation();
  const create = useCreateSpecLocation();
  const remove = useDeleteSpecLocation();
  const [open, setOpen] = useState(false);
  // The room the confirm asks about, kept through its exit.
  const [confirming, setConfirming] = useState<{ open: boolean; room: SpecLinesLocation | null }>({
    open: false,
    room: null,
  });

  const specific = line.locationType === "specific";
  const checked = checkedPlacements(line.locations, rooms);
  const checkedIds = checked.map((placement) => placement.locationId);
  const disabled = line.status === "finalized";
  const inError = updateType.isError || updateRooms.isError;
  // Each row's `Deleting...` is its own deletes', not the hook's latest call:
  // the menu stays open behind the confirm, so another delete — of another
  // room or the same one — can start while one runs, and a row keeps the state
  // until every delete of it has answered. A new delete takes the snackbar
  // down, as the donor's hook state does, and every failure brings it back
  // with a fresh timer (story 32).
  const [deletingIds, setDeletingIds] = useState<readonly number[]>([]);
  const [deleteFailure, setDeleteFailure] = useState<number | null>(null);
  const failures = useRef(0);

  const pickType = (locationType: LineLocationType) => {
    updateType.mutate({ ...target(), lineId: line.id, locationType });
    if (locationType !== "specific") setOpen(false);
  };

  // The donor's multiple `Select` value: the ticked set with the toggled room
  // added at the end or taken out.
  const toggleRoom = (locationId: number) => {
    const locationIds = checkedIds.includes(locationId)
      ? checkedIds.filter((id) => id !== locationId)
      : [...checkedIds, locationId];
    updateRooms.mutate({ ...target(), lineId: line.id, locationIds });
  };

  return (
    <>
      <div
        data-testid="location-selector"
        data-error={inError || undefined}
        // `FormControl`: inline, a column, as narrow as its content allows.
        className="relative inline-flex min-w-0 flex-col align-top"
      >
        <OutlinedField
          label=""
          shrink={false}
          size="small"
          invalid={inError}
          disabled={disabled}
          focused={open}
          className="w-auto min-w-0"
        >
          <MuiMultiSelect
            // Named by what it shows; the donor's combobox has no name at all
            // (no `labelId`) — the better tree, kept for the decision menu.
            id={displayId}
            aria-labelledby={displayId}
            open={open}
            onOpenChange={setOpen}
            disabled={disabled}
            disablePortal
            // MUI's `hasSelectedItemInListRef`: a ticked room times the
            // opening press's release.
            hasSelected={specific && checkedIds.length > 0}
            display={locationsSelectText(line.locationType, checked)}
            // `InputBase`'s root: body1 and the text cursor, both of which the
            // in-place menu inherits — so its backdrop, the list's padding
            // and a radio row's blank ends show the I-beam. Full width, as the
            // root fills its `FormControl`: the Editor's name cell stretches
            // the control to the cell (#810), and the caret keeps to its edge.
            anchorClassName="flex w-full min-w-0 cursor-text items-center text-base leading-[1.4375em]"
            className={cn(
              // `.MuiSelect-select` at `size='small'`: 8.5px / 14px round a
              // 23px line, 32px kept for the caret, one line, clipped — and
              // `.MuiInputBase-input`'s `width: 100%`.
              "text-lennar-ink block w-full min-w-0 cursor-pointer truncate py-[8.5px] pr-8 pl-3.5 text-base leading-[23px] outline-none select-none",
              "aria-disabled:text-lennar-ink-disabled aria-disabled:cursor-default",
            )}
            caret={(isOpen) => (
              <MuiSelectCaret
                open={isOpen}
                className={disabled ? "text-lennar-action-disabled opacity-100" : undefined}
              />
            )}
          >
            {TYPE_RADIOS.map(({ value, label }) => (
              <li
                key={value}
                role="none"
                // `SelectInput`'s key-up on every child it clones
                // (`SelectInput.js:626-631`): Space is cancelled, which in
                // Chrome keeps a focused radio from activating.
                onKeyUp={(event) => {
                  if (event.key === " ") event.preventDefault();
                }}
                className={cn(
                  "pl-[25.6px]",
                  value === "specific" &&
                    specific &&
                    "border-spec-locations-divider border-b border-solid",
                )}
              >
                <TypeRadio
                  label={label}
                  checked={line.locationType === value}
                  onPick={() => pickType(value)}
                />
              </li>
            ))}
            {specific
              ? rooms.map((room) => (
                  <RoomRow
                    key={room.id}
                    room={room}
                    rooms={rooms}
                    checked={checkedIds.includes(room.id)}
                    deleting={deletingIds.includes(room.id)}
                    onToggle={() => toggleRoom(room.id)}
                    onRename={(name) => rename.mutate({ ...target(), locationId: room.id, name })}
                    onAskDelete={() => setConfirming({ open: true, room })}
                  />
                ))
              : null}
            {specific ? (
              <AddLocation
                rooms={rooms}
                adding={create.isPending}
                onAdd={(name) => create.mutate({ ...target(), lineId: line.id, name })}
              />
            ) : null}
          </MuiMultiSelect>
        </OutlinedField>
      </div>

      {/* The confirm portals out of the field, but its clicks still bubble
          the React tree to the row: stopped here, as the field stops its own. */}
      <div className="contents" onClick={(event) => event.stopPropagation()}>
        <ConfirmDeleteDialog
          open={confirming.open}
          onOpenChange={(next) => {
            if (!next) setConfirming((current) => ({ ...current, open: false }));
          }}
          label={`Location "${confirming.room?.name ?? ""}"`}
          description={LOCATION_DELETE_DESCRIPTION}
          // The donor component as the Editor draws it: content-sized, opening
          // on its paper (#805 enumeration E4, #807 Assumption 11).
          fullWidth={false}
          autoFocusConfirm={false}
          onConfirm={() => {
            const room = confirming.room;
            setConfirming((current) => ({ ...current, open: false }));
            // Closed at once; the row shows the in-flight state (Assumption 3).
            if (!room) return;
            setDeleteFailure(null);
            setDeletingIds((ids) => [...ids, room.id]);
            void remove
              .mutateAsync({ ...target(), locationId: room.id })
              .catch(() => setDeleteFailure(++failures.current))
              .finally(() => setDeletingIds((ids) => withoutOne(ids, room.id)));
          }}
        />
      </div>

      <ErrorSnackbar
        open={updateType.isError}
        message={ERRORS.UPDATE_LINE_LOCATION_TYPE}
        onClose={updateType.reset}
      />
      <ErrorSnackbar
        open={updateRooms.isError}
        message={ERRORS.UPDATE_LINE_LOCATIONS}
        onClose={updateRooms.reset}
      />
      <ErrorSnackbar
        open={rename.isError}
        message={ERRORS.EDIT_SPECIFIC_LOCATION_NAME}
        onClose={rename.reset}
      />
      <ErrorSnackbar
        open={create.isError}
        message={ERRORS.CREATE_SPECIFIC_LOCATION}
        onClose={create.reset}
      />
      <ErrorSnackbar
        key={deleteFailure ?? "none"}
        open={deleteFailure !== null}
        message={ERRORS.DELETE_SPECIFIC_LOCATION}
        onClose={() => setDeleteFailure(null)}
      />
    </>
  );
}

/**
 * One type radio — MUI's `FormControlLabel` round an unsized `Radio`: a 42px
 * round hit box (9px round a 24px glyph) pulled 11px into the row, the label
 * 16px after it in body1. The `Radio` is a real radio input laid over its
 * glyph, as MUI's is; a click on it — or on the label, which forwards one —
 * sends the type, checked or not (`LineLocations.tsx:163-166`).
 */
function TypeRadio({
  label,
  checked,
  onPick,
}: {
  label: string;
  checked: boolean;
  onPick: () => void;
}) {
  // `SwitchBase` passes `centerRipple`: the radio's disc ripples from its centre.
  const { rippleTargetProps, rippleLayer } = useTouchRipple({ center: true });
  return (
    <label className="mr-4 -ml-[11px] inline-flex cursor-pointer items-center align-middle [-webkit-tap-highlight-color:transparent]">
      <span
        {...rippleTargetProps}
        className={cn(
          "relative inline-flex rounded-full p-[9px]",
          "hover:bg-lennar-primary-hover has-[:focus-visible]:bg-lennar-primary-focus",
          checked ? "text-lennar-blue-600" : "text-lennar-ink-secondary",
        )}
      >
        <input
          type="radio"
          checked={checked}
          readOnly
          onClick={(event) => {
            event.stopPropagation();
            onPick();
          }}
          className="absolute inset-0 z-1 m-0 size-full cursor-[inherit] p-0 opacity-0"
        />
        {checked ? <MuiRadioCheckedMediumIcon /> : <MuiRadioUncheckedMediumIcon />}
        {rippleLayer}
      </span>
      <span className="text-base leading-6">{label}</span>
    </label>
  );
}

/**
 * A name form inside the menu — the rename's and the add's
 * (`LineLocations.tsx:309-354, 434-463`): a small focused field, its keys and
 * clicks kept from the menu — so the typeahead never sees a letter, Escape
 * never closes it, and the row never toggles under a click in the box — and
 * the check absolute at its end, held, Enter with it, while `held`.
 */
function MenuNameForm({
  formRef,
  className,
  inputName,
  value,
  onChange,
  held,
  primaryCheck,
  onSubmit,
}: {
  formRef?: Ref<HTMLFormElement>;
  className?: string;
  /** The input's `name` — the add field's `name='name'`, which the browser keys its autofill on. */
  inputName?: string;
  value: string;
  onChange: (value: string) => void;
  held: boolean;
  /** The rename's check is `primary.main`; the add's takes the default ink. */
  primaryCheck: boolean;
  onSubmit: () => void;
}) {
  const stop = (event: SyntheticEvent) => event.stopPropagation();
  return (
    <form
      ref={formRef}
      className={cn("flex w-full flex-col", className)}
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!held) onSubmit();
      }}
    >
      <div className="relative flex w-full items-center">
        <div className="w-full" onKeyDown={stop} onClick={stop}>
          <TextField
            label=""
            size="small"
            autoFocus
            name={inputName}
            value={value}
            onChange={onChange}
          />
        </div>
        <IconButton
          type="submit"
          disabled={held}
          onClick={stop}
          className={cn("absolute right-[6px] p-[4.8px]", primaryCheck && "text-lennar-blue-600")}
        >
          <MuiCheckIcon />
        </IconButton>
      </div>
    </form>
  );
}

/** `ids` with one occurrence of `id` taken out — one delete's answer. */
function withoutOne(ids: readonly number[], id: number) {
  const at = ids.indexOf(id);
  return at === -1 ? ids : [...ids.slice(0, at), ...ids.slice(at + 1)];
}

/**
 * A room row's glyph: hidden until the row's `ListItem` is hovered — the
 * donor's `&:hover .edit-location-button`, and nothing else: no focus reveals
 * it, and neither does the `MenuItem`'s padding round the `ListItem`.
 */
const ROW_GLYPH = "text-lennar-blue-600 hidden shrink-0 cursor-pointer group-hover/item:block";

/**
 * One room of the checklist — the donor's `MenuItem` round `LocationItem`
 * (`LineLocations.tsx:199-211, 263-394`).
 *
 * At rest: the checkbox — MUI's bare `Checkbox`, with its own hover disc and
 * centred ripple inside the row's — the name, and, revealed while the
 * `ListItem` is hovered, the delete glyph, then the pencil, each 24px in the
 * primary ink and each stopping its click.
 *
 * Renaming: a small focused field with the check at its end.
 *
 *   - A press anywhere outside the form leaves it without saving; Escape does
 *     nothing (carried). The typed text stays on the row, and the pencil opens
 *     on it again, until the menu closes (carried — the donor's row draws its
 *     own `name` state).
 *   - The check is held, and Enter with it, while the typed name exactly equals
 *     another room's (case-sensitive, untrimmed — the donor's gate) **or is
 *     blank once trimmed**: blank no longer deletes (Assumption 6).
 *   - A name unchanged from the one the pencil opened on — compared as typed —
 *     sends nothing; otherwise the trimmed name is sent, optimistically.
 *
 * Once a rename is sent the row shows the cached name (Assumption 7): a refused
 * rename shows the old name back once the guess rolls back.
 *
 * While the room's delete runs the row reads `Deleting...` over a spinner and
 * stays live, as the donor's does (its `ListItem` takes no `disabled`).
 */
function RoomRow({
  room,
  rooms,
  checked,
  deleting,
  onToggle,
  onRename,
  onAskDelete,
}: {
  room: SpecLinesLocation;
  rooms: readonly SpecLinesLocation[];
  checked: boolean;
  deleting: boolean;
  onToggle: () => void;
  onRename: (name: string) => void;
  onAskDelete: () => void;
}) {
  const { rippleTargetProps, rippleLayer } = useTouchRipple();
  // The row holds two `ButtonBase`s, the `MenuItem` and its `Checkbox`: a press
  // on the tick runs both, the inner centred (`SwitchBase`'s `centerRipple`).
  const tick = useTouchRipple({ center: true });
  const formRef = useRef<HTMLFormElement>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(room.name);
  // The room's name when the pencil opened the field: an unchanged submit is
  // measured against it, so a cache write landing mid-edit is never reverted.
  const [startedFrom, setStartedFrom] = useState(room.name);
  // A cancelled rename's text, which the row shows until the menu closes.
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) return;
    const leave = (event: MouseEvent) => {
      if (formRef.current?.contains(event.target as Node)) return;
      setDraft(name);
      setEditing(false);
    };
    document.addEventListener("mousedown", leave);
    return () => document.removeEventListener("mousedown", leave);
  }, [editing, name]);

  const held = rooms.some((other) => other.id !== room.id && other.name === name) || !name.trim();

  return (
    <li
      role="option"
      data-menu-item=""
      data-testid={`location-${room.id}`}
      aria-selected={checked}
      tabIndex={-1}
      {...composeRippleProps<HTMLLIElement>(rippleTargetProps, {})}
      onKeyUp={(event) => {
        // `SelectInput`'s key-up (`SelectInput.js:626-631`).
        if (event.key === " ") event.preventDefault();
      }}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (!event.repeat || event.key === "Enter") onToggle();
        }
      }}
      className={cn(
        MUI_MENU_ITEM,
        // `MenuItem`'s `whiteSpace: nowrap`: a long name stays on one line,
        // the paper's overflow clipping it.
        "cursor-pointer whitespace-nowrap select-none",
        "aria-selected:bg-lennar-primary-selected",
        "aria-selected:hover:bg-lennar-primary-selected-hover",
        "aria-selected:focus-visible:bg-lennar-primary-selected-focus",
        "aria-selected:focus-visible:hover:bg-lennar-primary-selected-hover",
      )}
    >
      {editing ? (
        <MenuNameForm
          formRef={formRef}
          value={name}
          onChange={setName}
          held={held}
          primaryCheck
          onSubmit={() => {
            if (name !== startedFrom) onRename(name.trim());
            setDraft(null);
            setEditing(false);
          }}
        />
      ) : (
        // `ListItem`: space-between, 8px / 16px — and the box whose hover
        // reveals the glyphs, not the `MenuItem`'s padding round it.
        <div className="group/item relative flex w-full items-center justify-between px-4 py-2 text-left">
          {deleting ? (
            <CircularProgress size={24} className="mr-2" />
          ) : (
            // MUI's unsized `Checkbox`, a picture of the row's state: the row
            // is the option, so the box announces nothing of its own.
            <span
              aria-hidden="true"
              {...tick.rippleTargetProps}
              className={cn(
                "relative inline-flex rounded-full p-[9px]",
                "hover:bg-lennar-primary-hover",
                checked ? "text-lennar-blue-600" : "text-lennar-ink-secondary",
              )}
            >
              {checked ? <MuiCheckBoxIcon /> : <MuiCheckBoxOutlineBlankIcon />}
              {tick.rippleLayer}
            </span>
          )}
          {/* `ListItemText` (`sx: display flex, alignItems center`), its 4px
              margins zeroed inside a `MenuItem`. */}
          <div className="flex min-w-0 flex-auto items-center">
            <span data-slot="location-name" className="block text-base leading-6">
              {deleting ? "Deleting..." : (draft ?? room.name)}
            </span>
          </div>
          <button
            type="button"
            aria-label={`Delete ${room.name}`}
            // The donor's pencil is a bare glyph, never a tab stop; the delete
            // beside it follows it.
            tabIndex={-1}
            onClick={(event) => {
              event.stopPropagation();
              onAskDelete();
            }}
            className={cn("mr-2", ROW_GLYPH)}
          >
            <MuiDeleteOutlineOutlinedIcon />
          </button>
          <button
            type="button"
            aria-label={`Rename ${room.name}`}
            tabIndex={-1}
            onClick={(event) => {
              event.stopPropagation();
              setName(draft ?? room.name);
              setStartedFrom(room.name);
              setEditing(true);
            }}
            className={ROW_GLYPH}
          >
            <MuiEditIcon />
          </button>
        </div>
      )}
      {rippleLayer}
    </li>
  );
}

/**
 * `+ Add Location` — the donor's `AddLocationButton`
 * (`LineLocations.tsx:396-487`): a full-width text button with the add icon,
 * `pl 4 py 1.5`, in the menu's own font size and ink. While the room is
 * created it is held and reads `Adding...` with the loading spinner in its
 * icon's place — and, held, it takes the theme's disabled look, the primary
 * ink at half strength, over the inherited one.
 *
 * Its field holds the check while the trimmed name is blank or the name as
 * typed exactly equals a room's; the submit sends the name as typed, then
 * clears and closes the field at once. No press outside closes it (carried).
 */
function AddLocation({
  rooms,
  adding,
  onAdd,
}: {
  rooms: readonly SpecLinesLocation[];
  adding: boolean;
  onAdd: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const held = name.trim() === "" || rooms.some((room) => room.name === name);

  return (
    // `AddLocationButton` spreads none of the props `SelectInput` clones
    // onto it: no press flag, no key-up.
    <li role="none" data-select-unbound="">
      {open ? (
        <MenuNameForm
          className="mt-2 px-2"
          inputName="name"
          value={name}
          onChange={setName}
          held={held}
          primaryCheck={false}
          onSubmit={() => {
            onAdd(name);
            setName("");
            setOpen(false);
          }}
        />
      ) : (
        // A plain tab stop, as the donor's is — off the arrows' walk, reached
        // by Shift+Tab from the paper or a Tab past a name field.
        <MuiButton
          variant="text"
          loading={adding}
          disabled={adding}
          startIcon={<MuiAddIcon />}
          onClick={() => setOpen(true)}
          // Held, the theme's disabled medium button outranks the inherited
          // size: 0.875rem (`theme.ts:322-360`).
          className="disabled:text-lennar-blue-600 w-full justify-start py-3 pl-8 text-[length:inherit] leading-[1.75] text-inherit normal-case disabled:text-[length:0.875rem]"
        >
          {adding ? "Adding..." : "Add Location"}
        </MuiButton>
      )}
    </li>
  );
}
